// src/components/reports/SummaryReportModal.js

import React, { useState, useEffect } from 'react';
import { X, Download, FileText, Eye, FileSpreadsheet, FileDown } from 'lucide-react'; 
import toast from 'react-hot-toast';
import { generateReportPDF } from './ReportGenerator'; 
import { generateCSV, generateExcel } from '../../utils/exportUtils';
import { api } from '../../api';

const FORMAT_KEY = 'report_export_format';

const SummaryReportModal = ({ 
    isOpen, 
    onClose, 
    summary, 
    timeEntries, 
    properties, 
    selectedProperty, 
    dateFilter, 
    customDate,
    payrollData,
    user,
    userTimezone
}) => {
    const [format, setFormat] = useState(() => localStorage.getItem(FORMAT_KEY) || 'PDF');

    useEffect(() => {
        localStorage.setItem(FORMAT_KEY, format);
    }, [format]);

    if (!isOpen) return null;

    const currentProperty = properties.find(p => p.id === parseInt(selectedProperty));

    const getDateRange = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        if (dateFilter === 'firstHalf') {
            return { start: `${year}-${String(month+1).padStart(2,'0')}-01`, end: `${year}-${String(month+1).padStart(2,'0')}-15` };
        } else if (dateFilter === 'secondHalf') {
            const lastDay = new Date(year, month + 1, 0).getDate();
            return { start: `${year}-${String(month+1).padStart(2,'0')}-16`, end: `${year}-${String(month+1).padStart(2,'0')}-${lastDay}` };
        } else if (dateFilter === 'today') {
            const today = now.toISOString().slice(0,10);
            return { start: today, end: today };
        } else if (dateFilter === 'custom' && customDate) {
            return { start: customDate.start, end: customDate.end };
        }
        return { start: null, end: null };
    };

    const saveToHistory = async (fileType, fileName, blob) => {
        try {
            const { start, end } = getDateRange();
            const base64 = await blobToBase64(blob);

            await api.saveReportHistory({
                propertyId: parseInt(selectedProperty),
                reportType: 'COMBINED',
                dateFilter: dateFilter || 'custom',
                dateRangeStart: start,
                dateRangeEnd: end,
                pdfData: base64,
                fileName,
                fileType,
            });
        } catch (err) {
            console.error('Failed to save report to history:', err);
        }
    };

    const blobToBase64 = (blob) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    const handleDownload = async () => {
        if (format === 'PDF') {
            const doc = generateReportPDF('COMBINED', {
                summary, timeEntries, currentProperty, dateFilter, customDate, payrollData, user, timeZone: userTimezone
            });
            if (doc) {
                const reportName = 'Summary_Report';
                const fileName = `${reportName}_${new Date().toISOString().slice(0,10)}.pdf`;
                const pdfBase64 = doc.output('datauristring').split(',')[1];
                const { start, end } = getDateRange();

                try {
                    await api.saveReportHistory({
                        propertyId: parseInt(selectedProperty),
                        reportType: 'COMBINED',
                        dateFilter: dateFilter || 'custom',
                        dateRangeStart: start,
                        dateRangeEnd: end,
                        pdfData: pdfBase64,
                        fileName,
                        fileType: 'PDF',
                    });
                } catch (err) {
                    console.error('Failed to save report to history:', err);
                }

                doc.save(fileName);
                toast.success("Report Downloaded");
                onClose();
            }
        } else {
            const exportData = { summary, timeEntries, reportType: 'COMBINED', dateFilter, customDate, userTimezone, propertyName: currentProperty?.name };
            const { fileName, blob } = format === 'CSV' ? generateCSV(exportData) : generateExcel(exportData);
            await saveToHistory(format === 'CSV' ? 'CSV' : 'XLSX', fileName, blob);
            toast.success("Report Downloaded");
            onClose();
        }
    };

    const handlePreview = async () => {
        const doc = generateReportPDF('COMBINED', {
            summary, timeEntries, currentProperty, dateFilter, customDate, payrollData, user, timeZone: userTimezone
        });

        if (doc) {
            const reportName = 'Summary_Report';
            const fileName = `${reportName}_${new Date().toISOString().slice(0,10)}.pdf`;
            const pdfBase64 = doc.output('datauristring').split(',')[1];
            const { start, end } = getDateRange();

            try {
                await api.saveReportHistory({
                    propertyId: parseInt(selectedProperty),
                    reportType: 'COMBINED',
                    dateFilter: dateFilter || 'custom',
                    dateRangeStart: start,
                    dateRangeEnd: end,
                    pdfData: pdfBase64,
                    fileName,
                    fileType: 'PDF',
                });
            } catch (err) {
                console.error('Failed to save report to history:', err);
            }

            const pdfBlob = doc.output('blob');
            const blobUrl = URL.createObjectURL(pdfBlob);
            window.open(blobUrl, '_blank');
        }
    };

    const formatOptions = [
        { key: 'PDF', icon: FileText, label: 'PDF' },
        { key: 'CSV', icon: FileDown, label: 'CSV' },
        { key: 'XLSX', icon: FileSpreadsheet, label: 'Excel' },
    ];

    return (
        <div className="modal-backdrop">
            <div className="modal-content" style={{ maxWidth: '500px', width: '92%', padding: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                    <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1.25rem' }}>
                        <FileText size={22} className="text-primary" />
                        Export Report
                    </h2>
                    <button onClick={onClose} className="icon-button" style={{ flexShrink: 0 }}><X size={20} /></button>
                </div>
                
                <p style={{ color: '#64748b', marginBottom: '16px', fontSize: '0.9rem', lineHeight: '1.4' }}>
                    Export report for <strong style={{ wordBreak: 'break-word' }}>{currentProperty ? currentProperty.name : 'All Properties'}</strong>
                </p>

                {/* Format Toggle */}
                <div style={{ display: 'flex', gap: '6px', marginBottom: '20px' }}>
                    {formatOptions.map(opt => {
                        const Icon = opt.icon;
                        const isActive = format === opt.key;
                        return (
                            <button
                                key={opt.key}
                                onClick={() => setFormat(opt.key)}
                                style={{
                                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                                    padding: '10px 8px', borderRadius: '0.5rem', border: '1px solid',
                                    borderColor: isActive ? '#1798c1' : '#e2e8f0',
                                    backgroundColor: isActive ? '#f0f9ff' : '#fff',
                                    color: isActive ? '#0369a1' : '#64748b',
                                    cursor: 'pointer', fontWeight: isActive ? 600 : 400, fontSize: '0.8rem',
                                    transition: 'all 0.15s',
                                }}
                            >
                                <Icon size={15} />
                                {opt.label}
                            </button>
                        );
                    })}
                </div>

                {payrollData && (
                    <div style={{ 
                        backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', 
                        padding: '10px', borderRadius: '6px', marginBottom: '20px', fontSize: '0.85em', color: '#0369a1'
                    }}>
                        <strong>Payment Statements:</strong> {payrollData.status}
                    </div>
                )}

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '10px' }}>
                    {format === 'PDF' && (
                        <button 
                            onClick={handlePreview} 
                            className="button button-secondary" 
                            title="Preview PDF in new tab"
                            style={{ flex: 1, display:'flex', justifyContent:'center', alignItems:'center', height:'44px', gap: '6px', fontSize: '0.875rem' }}
                        >
                            <Eye size={17} />
                            <span>Preview</span>
                        </button>
                    )}

                    <button 
                        onClick={handleDownload} 
                        className="button action-button" 
                        title={`Download ${format}`}
                        style={{ flex: 1, display:'flex', justifyContent:'center', alignItems:'center', height:'44px', gap: '6px', fontSize: '0.875rem' }}
                    >
                        <Download size={17} />
                        <span>Download {format === 'XLSX' ? 'Excel' : format}</span>
                    </button>
                </div>
                
                {!payrollData && (
                    <p style={{textAlign:'center', fontSize:'0.75rem', color:'#94a3b8', marginTop:'10px', marginBottom: 0}}>
                        * Payment Statement data is not included as no statement exists for this period.
                    </p>
                )}

                {/* --- Branding Footer --- */}
                <div style={{
                    marginTop: '20px', paddingTop: '12px', borderTop: '1px solid #f1f5f9',
                    textAlign: 'center', fontSize: '0.7rem', color: '#94a3b8'
                }}>
                    Product of{' '}
                    <a 
                        href="https://thewesterntech.com/" target="_blank" rel="noopener noreferrer"
                        style={{ color: '#1798c1', textDecoration: 'none', fontWeight: '600' }}
                    >
                        The WesternTech
                    </a>
                </div>
            </div>
        </div>
    );
};

export default SummaryReportModal;
